import { useState, useEffect } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "@/hooks/use-toast";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Lightbulb, Send } from "lucide-react";

const CATEGORIES = [
  { value: "game_idea", label: "💡 Ιδέα για παιχνίδι" },
  { value: "feature", label: "⚙️ Νέο feature" },
  { value: "bug", label: "🐛 Πρόβλημα/Bug" },
  { value: "improvement", label: "✨ Βελτίωση" },
  { value: "general", label: "💬 Γενικό σχόλιο" },
];

export default function SuggestionBox() {
  const { user } = useAuth();
  const [open, setOpen] = useState(false);
  const [message, setMessage] = useState("");
  const [category, setCategory] = useState("general");
  const [sending, setSending] = useState(false);
  const [displayName, setDisplayName] = useState("Χρήστης");

  useEffect(() => {
    if (!user) return;
    import("@/lib/profileNameCache").then(({ getCachedProfileName }) =>
      getCachedProfileName(user.id).then((data) => {
        if (data) setDisplayName(data.display_name || data.username || "Χρήστης");
      })
    );
  }, [user]);

  if (!user) return null;

  const handleSend = async () => {
    const trimmed = message.trim();
    if (trimmed.length < 5) {
      toast({ title: "Γράψε τουλάχιστον 5 χαρακτήρες", variant: "destructive" });
      return;
    }
    if (trimmed.length > 1000) {
      toast({ title: "Πολύ μεγάλο μήνυμα (max 1000)", variant: "destructive" });
      return;
    }

    setSending(true);
    const { error } = await supabase.from("user_suggestions" as any).insert({
      user_id: user.id,
      user_name: displayName,
      category,
      message: trimmed,
    } as any);

    setSending(false);

    if (error) {
      toast({ title: "Κάτι πήγε στραβά", description: error.message, variant: "destructive" });
      return;
    }

    toast({ title: "✅ Η πρότασή σου στάλθηκε!", description: "Ευχαριστούμε πολύ! Θα το δούμε σύντομα." });
    setMessage("");
    setCategory("general");
    setOpen(false);
  };

  return (
    <>
      <button
        onClick={() => setOpen(true)}
        className="fixed bottom-20 right-4 z-50 bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600 text-white rounded-full p-3 shadow-lg hover:shadow-xl transition-all duration-200 hover:scale-110 group"
        title="Στείλε ιδέα ή πρόταση"
      >
        <Lightbulb className="h-5 w-5 group-hover:animate-pulse" />
      </button>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-lg">
              <Lightbulb className="h-5 w-5 text-amber-500" />
              Στείλε Ιδέα ή Πρόταση
            </DialogTitle>
            <DialogDescription>
              Μοιράσου τη σκέψη σου μαζί μας! Κάθε πρόταση μετράει 💛
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 mt-2">
            <div>
              <label className="text-sm font-medium text-muted-foreground mb-1.5 block">Κατηγορία</label>
              <Select value={category} onValueChange={setCategory}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {CATEGORIES.map((c) => (
                    <SelectItem key={c.value} value={c.value}>
                      {c.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div>
              <label className="text-sm font-medium text-muted-foreground mb-1.5 block">Η ιδέα σου</label>
              <Textarea
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                placeholder="Γράψε εδώ τι θα ήθελες να δεις στο zizanio.gr..."
                rows={4}
                maxLength={1000}
                className="resize-none"
              />
              <p className="text-xs text-muted-foreground mt-1 text-right">{message.length}/1000</p>
            </div>

            <Button
              onClick={handleSend}
              disabled={sending || message.trim().length < 5}
              className="w-full bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600"
            >
              {sending ? (
                <span className="animate-spin mr-2">⏳</span>
              ) : (
                <Send className="h-4 w-4 mr-2" />
              )}
              Αποστολή
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
